import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { CrmTask } from './types';
import type { RootStore } from './RootStore';

export class TaskStore {
  tasks = new Map<string, CrmTask>();
  private root: RootStore;

  // Secondary indexes (plain, not observable)
  private _contactIdIndex = new Map<string, Set<string>>();
  private _dealIdIndex = new Map<string, Set<string>>();
  private _projectIdIndex = new Map<string, Set<string>>();

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, {
      all: computed,
      open: computed,
      inProgress: computed,
      completed: computed,
    });
  }

  // ── Index helpers ──────────────────────────────────────────────────────────

  private _addToIndex(index: Map<string, Set<string>>, key: string | null | undefined, id: string): void {
    if (!key) return;
    if (!index.has(key)) index.set(key, new Set());
    index.get(key)!.add(id);
  }

  private _removeFromIndex(index: Map<string, Set<string>>, key: string | null | undefined, id: string): void {
    if (!key) return;
    index.get(key)?.delete(id);
  }

  private _set(id: string, task: CrmTask): void {
    const existing = this.tasks.get(id);
    if (existing) {
      if (existing.contactId !== task.contactId) {
        this._removeFromIndex(this._contactIdIndex, existing.contactId, id);
        this._addToIndex(this._contactIdIndex, task.contactId, id);
      }
      if (existing.dealId !== task.dealId) {
        this._removeFromIndex(this._dealIdIndex, existing.dealId, id);
        this._addToIndex(this._dealIdIndex, task.dealId, id);
      }
      if (existing.projectId !== task.projectId) {
        this._removeFromIndex(this._projectIdIndex, existing.projectId, id);
        this._addToIndex(this._projectIdIndex, task.projectId, id);
      }
    } else {
      this._addToIndex(this._contactIdIndex, task.contactId, id);
      this._addToIndex(this._dealIdIndex, task.dealId, id);
      this._addToIndex(this._projectIdIndex, task.projectId, id);
    }
    this.tasks.set(id, task);
  }

  private _delete(id: string): void {
    const existing = this.tasks.get(id);
    if (existing) {
      this._removeFromIndex(this._contactIdIndex, existing.contactId, id);
      this._removeFromIndex(this._dealIdIndex, existing.dealId, id);
      this._removeFromIndex(this._projectIdIndex, existing.projectId, id);
    }
    this.tasks.delete(id);
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get all(): CrmTask[] {
    const result: CrmTask[] = [];
    for (const t of this.tasks.values()) {
      if (!t.isDeleted) result.push(t);
    }
    return result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  get open(): CrmTask[] { return this.all.filter(t => t.status === 0); }
  get inProgress(): CrmTask[] { return this.all.filter(t => t.status === 1); }
  get completed(): CrmTask[] { return this.all.filter(t => t.status === 2); }

  // ── Queries ────────────────────────────────────────────────────────────────

  getById(id: string): CrmTask | undefined {
    const t = this.tasks.get(id);
    return t && !t.isDeleted ? t : undefined;
  }

  getByContactId(contactId: string): CrmTask[] {
    const ids = this._contactIdIndex.get(contactId);
    if (!ids || ids.size === 0) return [];
    const result: CrmTask[] = [];
    for (const id of ids) {
      const t = this.tasks.get(id);
      if (t && !t.isDeleted) result.push(t);
    }
    return result;
  }

  getByDealId(dealId: string): CrmTask[] {
    const ids = this._dealIdIndex.get(dealId);
    if (!ids || ids.size === 0) return [];
    const result: CrmTask[] = [];
    for (const id of ids) {
      const t = this.tasks.get(id);
      if (t && !t.isDeleted) result.push(t);
    }
    return result;
  }

  getByProjectId(projectId: string): CrmTask[] {
    const ids = this._projectIdIndex.get(projectId);
    if (!ids || ids.size === 0) return [];
    const result: CrmTask[] = [];
    for (const id of ids) {
      const t = this.tasks.get(id);
      if (t && !t.isDeleted) result.push(t);
    }
    return result;
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const r of records) {
        this._set(r.id as string, r as unknown as CrmTask);
      }
    });
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const c of changes) {
        if (c.isDeleted) this._delete(c.id);
        else if (c.data) this._set(c.id, c.data as unknown as CrmTask);
      }
    });
  }

  removeFromMap(id: string): void {
    this._delete(id);
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  create(data: Partial<CrmTask>): CrmTask {
    const t: CrmTask = {
      id: crypto.randomUUID(), title: data.title ?? '', description: data.description,
      status: data.status ?? 0, priority: data.priority ?? 1, dueDate: data.dueDate,
      contactId: data.contactId, dealId: data.dealId, projectId: data.projectId, ownerId: data.ownerId,
      orgId: '', syncVersion: 0, isDeleted: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    this._set(t.id, t);
    this.root.syncStore.queueMutation('task', 'create', t.id, t);
    return t;
  }

  update(id: string, patch: Partial<CrmTask>): void {
    const existing = this.tasks.get(id);
    if (!existing) return;
    this._set(id, { ...existing, ...patch, updatedAt: new Date().toISOString() });
    this.root.syncStore.queueMutation('task', 'update', id, patch);
  }

  delete(id: string): void {
    const existing = this.tasks.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: true, updatedAt: new Date().toISOString() });
    }
    this.root.syncStore.queueMutation('task', 'delete', id);
  }

  restore(id: string): void {
    const existing = this.tasks.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: false, updatedAt: new Date().toISOString() });
      this.root.syncStore.queueMutation('task', 'update', id, { isDeleted: false });
    }
  }

  clear(): void {
    this.tasks.clear();
    this._contactIdIndex.clear();
    this._dealIdIndex.clear();
    this._projectIdIndex.clear();
  }
}
